import {
  PROFILE_COLORS,
  PROFILE_COLOR_IDS,
  resolveSeatHues,
  type RoomState,
} from '@tv-casino/shared';

/**
 * Jackbox-style seat colours: every seat owns one hue for the night, so the
 * room can read whose chips are whose on the felt. Seven hues picked to stay
 * apart on dark baize and away from the felt's own red pockets and green
 * zeroes — the same seven a profile picks from, in the same order.
 */
export const PLAYER_COLORS = PROFILE_COLOR_IDS.map((id) => PROFILE_COLORS[id].hex);

/** The by-index colour seat N wears when nobody at the table picked one. */
export const playerColor = (seatIndex: number): string =>
  PLAYER_COLORS[
    ((seatIndex % PLAYER_COLORS.length) + PLAYER_COLORS.length) % PLAYER_COLORS.length
  ]!;

const channels = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

/**
 * The lit top of a chip or disc: the colour 55% mixed with white. Worked out
 * here rather than with CSS color-mix(), which the Cast receiver's Chromium
 * doesn't know — it would throw away the chip's whole background.
 */
export const hexHighlight = (hex: string): string =>
  `#${channels(hex)
    .map((c) =>
      Math.round(c * 0.55 + 255 * 0.45)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/** The colour at `alpha` opacity, as rgba() — same Cast-safe reason as above. */
export const hexAlpha = (hex: string, alpha: number): string =>
  `rgba(${channels(hex).join(', ')}, ${alpha})`;

/** The lit top of seat N's by-index chip. */
export const playerHighlight = (seatIndex: number): string => hexHighlight(playerColor(seatIndex));

/** Seat index → the hue that seat wears on this table right now. */
export type SeatHue = (seatIndex: number) => string;

/**
 * The table's seat colours, honouring profile picks. Every screen derives
 * this from the same `room_state`, so the TV plate, the felt's chips and the
 * phone agree without the server naming a colour per seat.
 */
export function seatHues(state: Pick<RoomState, 'players'> | null | undefined): SeatHue {
  if (!state) return playerColor;
  const resolved = resolveSeatHues(
    state.players.map((p) => ({ seats: p.seats, color: p.color ?? null })),
  );
  return (seatIndex) => {
    const id = resolved[seatIndex];
    return id ? PROFILE_COLORS[id].hex : playerColor(seatIndex);
  };
}

